import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  XIcon,
  ClockIcon,
  CheckCircleIcon,
  CancelIcon,
  TrophySmallIcon,
} from '../../../../assets/svg'
import locationIcon from '../../../../assets/img/location-icon.svg'
import calendarIcon from '../../../../assets/img/calendar-icon.svg'
import { REQUEST_STATUSES } from '../../../../assets/const'
import { getApplication } from '../../../../actions/participant'
import { setCurrentApplication } from '../../../../store/participant/applications/slice'
import { formatRange, formatSubmittedAt } from '../../../../utils/smallHelpers'
import Loader from '../../../../components/Loader'


const BACKEND_TO_UI_STATUS = {
  submitted: REQUEST_STATUSES.SUBMITTED,
  accepted: REQUEST_STATUSES.APPROVED,
  rejected: REQUEST_STATUSES.REJECTED,
  withdrawn: REQUEST_STATUSES.WITHDRAWN,
  completed: REQUEST_STATUSES.COMPLETED,
}

const STATUS_META = {
  [REQUEST_STATUSES.SUBMITTED]: { label: 'Подана', bg: '#f3f4f6', color: '#475569', Icon: ClockIcon },
  [REQUEST_STATUSES.APPROVED]: { label: 'Одобрена', bg: '#d1fada', color: '#228b51', Icon: CheckCircleIcon },
  [REQUEST_STATUSES.REJECTED]: { label: 'Отклонена', bg: '#fee2e2', color: '#dc2626', Icon: CancelIcon },
  [REQUEST_STATUSES.WITHDRAWN]: { label: 'Отозвана', bg: '#fee2e2', color: '#dc2626', Icon: CancelIcon },
  [REQUEST_STATUSES.COMPLETED]: { label: 'Завершено', bg: '#f3f4f6', color: '#475569', Icon: TrophySmallIcon },
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status]
  if (!meta) return null
  const { label, bg, color, Icon } = meta
  return (
    <span
      className="flex items-center gap-[5px] whitespace-nowrap rounded-[50px] px-[0.75rem] sm:px-[1rem] py-[0.375rem] sm:py-[0.5rem] text-[0.812rem] sm:text-[0.938rem] font-medium leading-[1.25rem]"
      style={{ backgroundColor: bg, color }}
    >
      <Icon />
      {label}
    </span>
  )
}

const FieldLabel = ({ children }) => (
  <span className="text-[0.812rem] text-[#9ca3af]">{children}</span>
)

const DetailsModal = ({ applicationId, onClose, onCancel }) => {

  const dispatch = useDispatch()
  const data = useSelector((state) => state.participant.applications.current)

  useEffect(() => {
    if (!applicationId) return
    dispatch(getApplication(applicationId))
    return () => {
      dispatch(setCurrentApplication(null))
    }
  }, [dispatch, applicationId])

  if (!applicationId) return null

  const isLoaded = data && data.id === applicationId

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(17,24,39,0.45)] p-[0.75rem] sm:p-[1rem]"
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[95vh] w-full max-w-[55rem] flex-col overflow-hidden rounded-[16px] sm:rounded-[20px] bg-white shadow-[0px_20px_60px_rgba(0,0,0,0.15)]"
      >
        {!isLoaded ? (
          <div className="flex items-center justify-between gap-[1rem] px-[1.25rem] sm:px-[2rem] py-[1.25rem]">
            <Loader size="md" color="#0d9488" />
            <button
              type="button"
              onClick={onClose}
              className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center rounded-[8px] border-0 bg-transparent hover:bg-[#f3f4f6] cursor-pointer"
            >
              <XIcon />
            </button>
          </div>
        ) : (
          <DetailsContent data={data} onClose={onClose} onCancel={onCancel} />
        )}
      </div>
    </div>
  )
}

const DetailsContent = ({ data, onClose, onCancel }) => {
  const uiStatus = BACKEND_TO_UI_STATUS[data.status]
  const participants = data.participants || []
  const supervisors = data.supervisors || []
  const event = data.event || {}
  const eventName = event.name || ''
  const eventLocation = event.location || ''
  const eventDates = formatRange(event.start_date, event.end_date)
  const institution = data.participant_org_name || ''
  const canCancel = data.status === 'submitted'

  return (
    <>
      {/* Шапка: название проекта + крестик */}
      <div className="flex items-start justify-between gap-[1rem] px-[1.25rem] sm:px-[2rem] pt-[1.25rem] sm:pt-[1.75rem] pb-[1rem] sm:pb-[1.25rem]">
        <div className="flex flex-col gap-[0.25rem] min-w-0">
          <FieldLabel>Название проекта</FieldLabel>
          <h2 className="m-0 text-[1.125rem] sm:text-[1.5rem] font-semibold leading-[1.4] sm:leading-[1.875rem] text-[#111827] break-words">
            {data.project_title || '—'}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center rounded-[8px] border-0 bg-transparent hover:bg-[#f3f4f6] cursor-pointer"
        >
          <XIcon />
        </button>
      </div>

      {/* Тело */}
      <div className="flex flex-1 flex-col gap-[1.25rem] sm:gap-[1.5rem] overflow-y-auto px-[1.25rem] sm:px-[2rem] pb-[1.25rem] sm:pb-[1.5rem]">
        {institution && (
          <div className="flex flex-col gap-[0.375rem]">
            <FieldLabel>Образовательное учреждение</FieldLabel>
            <span className="text-[0.938rem] sm:text-[1rem] text-[#111827] break-words">
              {institution}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr] gap-[1.25rem] sm:gap-[2rem]">
          {/* Участники */}
          <div className="flex flex-col gap-[1rem]">
            <FieldLabel>Участники</FieldLabel>
            {participants.length === 0 ? (
              <span className="text-[0.875rem] text-[#9ca3af] italic">—</span>
            ) : (
              participants.map((p) => (
                <div key={p.id} className="flex flex-col gap-[0.25rem]">
                  <span className="text-[0.938rem] sm:text-[1rem] text-[#111827] break-words">
                    {p.full_name}
                  </span>
                  <span className="text-[0.812rem] text-[#9ca3af]">
                    {p.section_number ? `Секция: ${p.section_number}` : 'Номер секции не указан'}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Руководители */}
          <div className="flex flex-col gap-[1rem]">
            <FieldLabel>Руководители</FieldLabel>
            {supervisors.length === 0 ? (
              <span className="text-[0.875rem] text-[#9ca3af] italic">—</span>
            ) : (
              supervisors.map((s) => {
                const meta = [s.position, s.academic_title, s.phone].filter(Boolean).join(', ')
                return (
                  <div key={s.id} className="flex flex-col gap-[0.25rem]">
                    <span className="text-[0.938rem] sm:text-[1rem] text-[#111827] break-words">
                      {s.full_name}
                    </span>
                    <span className="text-[0.812rem] text-[#9ca3af] break-words">
                      {meta || '—'}
                    </span>
                  </div>
                )
              })
            )}
          </div>

          {/* Необходимое оборудование */}
          <div className="flex flex-col gap-[1rem]">
            <FieldLabel>Необходимое оборудование</FieldLabel>
            <span className="text-[0.938rem] sm:text-[1rem] text-[#111827] break-words">
              {data.project_description || '—'}
            </span>
          </div>
        </div>

        {/* Карточка мероприятия */}
        <div className="flex flex-col gap-[1rem] rounded-[16px] border border-solid border-[#e5e7eb] bg-white p-[1.25rem] sm:p-[1.5rem]">
          <div className="flex flex-col gap-[0.75rem] sm:flex-row sm:items-start sm:justify-between sm:gap-[1rem]">
            <div className="flex flex-col gap-[0.625rem] min-w-0">
              <h3 className="m-0 text-[1.125rem] sm:text-[1.25rem] font-semibold leading-[1.4] sm:leading-[1.5rem] text-[#111827] break-words">
                {eventName || '—'}
              </h3>
              <div className="flex flex-col gap-[0.375rem] text-[0.875rem] sm:text-[0.938rem] text-[#6b7280]">
                {eventLocation && (
                  <div className="flex items-center gap-[0.5rem]">
                    <img src={locationIcon} alt="" className="h-[1.125rem] w-[1.125rem] shrink-0" />
                    <span className="break-words">{eventLocation}</span>
                  </div>
                )}
                {eventDates && (
                  <div className="flex items-center gap-[0.5rem]">
                    <img src={calendarIcon} alt="" className="h-[1.125rem] w-[1.125rem] shrink-0" />
                    <span className="break-words">{eventDates}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="self-start">
              <StatusBadge status={uiStatus} />
            </div>
          </div>

          <div className="text-[0.875rem] sm:text-[0.938rem] text-[#848484]">
            Дата подачи заявки:{' '}
            <span className="font-medium text-[#111827]">
              {formatSubmittedAt(data.submitted_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Подвал */}
      {canCancel && (
        <div className="flex items-center justify-center px-[1.25rem] sm:px-[2rem] pb-[1.25rem] sm:pb-[1.5rem] pt-[0.5rem]">
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer border-0 bg-transparent text-[0.938rem] sm:text-[1rem] font-medium text-[#dc2626] underline hover:text-[#b91c1c]"
          >
            Отменить подачу заявки
          </button>
        </div>
      )}
    </>
  )
}

export default DetailsModal
